/**
 * qr-decode.js — shared QR / barcode DECODE primitive for Timbrica.
 *
 * ONE decoder for every tool that reads a code (barcode-scanner image tab,
 * qr-code scanner tab, otpauth-qr-decoder, qr-scanner). Wraps the vendored
 * ZXing-C++ wasm build (`/vendor/zxing/`).
 *
 * WHY this exists — measured, not assumed (2026-07-10 benchmark, 536 real
 * BoofCV wild photos + 1149 zxing blackbox + 36 rendered phone screenshots):
 *   engine            wild-photo recall   screenshot recall   monitor-photo
 *   html5-qrcode          25.7%               77.8%               0%
 *   jsQR                  22.4%               88.9%               —
 *   ZXing-C++ (this)      74.1%              100.0%             100%
 *   ZXing-C++ + ladder    77.1%              100.0%             100%
 * The three legacy tools each shipped a DIFFERENT weak decoder (html5-qrcode,
 * BarcodeDetector-only, jsQR). qr-code's scanner used BarcodeDetector with NO
 * fallback — dead in every iOS browser, Firefox, and desktop Chrome/Edge on
 * Windows (verified: 'BarcodeDetector' in window === false in all of them).
 *
 * The "ladder": most uploads are a big screenshot where the code is a small
 * fraction of the frame, so ZXing sees only ~10px of module detail. Rung 1
 * reads the whole image (fast path, ~90% of hits). On miss it tiles the image
 * (2×2 then 3×3, 25% overlap) so each pass sees 3× the modules, then upscales
 * 2×. Same small-target trick that fixed face detection in /video-blur.
 * Every rung is bounded in PIXELS (see FIRST_PASS_MAX_SIDE / TILE_MAX_PIXELS):
 * the engine's memory only ever grows, so the biggest input it is ever handed
 * is the memory it keeps for the rest of the page's life.
 *
 * API (all async unless noted):
 *   window.__qrDecode.ready()                       -> Promise (lazy-loads wasm)
 *   window.__qrDecode.retry()                       -> Promise (ready() now, past the back-off)
 *   window.__qrDecode.isSupported()                 -> bool (WebAssembly present)
 *   window.__qrDecode.fromFile(file, opts)          -> Promise<Result[]>  (ladder ON by default)
 *   window.__qrDecode.fromImageData(imageData, opts)-> Promise<Result[]>  (ladder ON by default)
 *   window.__qrDecode.fromBitmap(imageBitmap, opts) -> Promise<Result[]>
 *   window.__qrDecode.fromCanvasFast(canvas)        -> Promise<Result[]>  (single pass, for live frames)
 *   window.__qrDecode.liveFrame(video, opts)        -> {canvas, map} | null (sync; what a live loop decodes)
 *   window.__qrDecode.liveCameraSize()              -> {width, height} (sync; what a live loop asks the camera for)
 *   window.__qrDecode.imageBitmap(blob, {maxSide})  -> Promise<ImageBitmap> (SVG too; ≤ maxSide)
 *   window.__qrDecode.isMemoryError(err)            -> bool (sync)
 *
 * opts: { tiling:true, formats:[], maxSymbols:16, signal }
 * Result: { text, format, bytes:Uint8Array|null, position:{topLeft,...}|null,
 *           gs1:bool, sym:string, enc?:string, rawText?:string,
 *           symbology: ']d2' | ']d1' | ']Q3' | … | null, contentType: 'GS1' | 'Text' | … | null }
 *   `format` is normalised to html5-qrcode-style names (QR_CODE, EAN_13,
 *   DATA_MATRIX, PDF_417, AZTEC, CODE_128, UPC_A, …) so existing downstream
 *   classifiers keep working unchanged. `position` is in the pixels of the
 *   source image, whichever rung found the code.
 *   `gs1` — the symbol itself says it carries GS1 element strings (FNC1 in the
 *   first position: GS1-128 «]C1», GS1 DataMatrix «]d2», GS1 QR «]Q3»); `sym` —
 *   the ISO symbology identifier. A digit string in a PLAIN Code 128 is not a
 *   GS1 message, and only the symbol can tell the two apart (see
 *   barcode-parsers.js classify).
 *   `enc` — the text was re-read from `bytes` in this encoding (`rawText` is
 *   what ZXing guessed): see settleText below.
 *
 * Failures are TYPED, not "no code" (2026-09-26):
 *   err.code === 'ENGINE' — the wasm engine did not start (or died). Until
 *     then a failed start was swallowed: ready() resolved anyway, zxing kept
 *     the failed module cached, every decode failed, and the ladder turned
 *     each failure into "no code" — a scanner that could never read anything
 *     and said "No code found" forever. The failed module is dropped now and
 *     the next ready() starts it again (after a short back-off, so a camera
 *     loop does not refetch the wasm every frame).
 *   err.code === 'MEMORY' — the image could not be held in memory at all.
 */
(function (w) {
  'use strict';
  if (w.__qrDecode) return;

  var WASM_URL = '/vendor/zxing/zxing_reader.wasm';
  var Z = null;             // ZXingWASM IIFE global once the <script> has run
  var readyPromise = null;
  var lastFail = null;      // { at, n, err } — the last failed start, for the back-off in ready()
  var failReported = false;

  /* The input ceilings. zxing-wasm's heap (a WebAssembly.Memory) can grow but
     never shrink, so whatever the largest decode needed stays allocated until
     the page closes. Measured 2026-09-26, Firefox 156, a 48 MP photo with no
     code: the old ladder handed the engine the whole JPEG (decoded inside the
     wasm at full size) and then 27 MP tiles — engine memory 21.3 → 236.9 MB
     for the rest of the visit (browser peak +661 MB; 70–84 s on a busier
     photo). The whole ladder with these ceilings, same photo, same run:
     21.3 → 55.7 MB, 28 s against the old 32 s. Tiles keep NATIVE detail (that
     is their whole point) — the grid just gets finer until no single tile is
     bigger than the cap. Anything up to 4096 px on the long side runs exactly
     as before (tests/playwright/qr-decode-bounded-memory-and-svg.mjs;
     recall above the ceiling: qr-decode-big-photo-recall-bench.mjs). */
  var FIRST_PASS_MAX_SIDE = 4096;
  var TILE_MAX_PIXELS = 4096 * 4096;        // 16.8 MP per decode input
  // An SVG has no pixels until we give it some: rasterised with its long side
  // in this range — a v40 QR (177 modules) still gets ~11 px per module.
  var SVG_MIN_SIDE = 1024, SVG_MAX_SIDE = 2048;

  /* zxing-wasm 3.1.0 never frees what it reads. Its read wrapper takes the
     embind vector of results from the engine, copies every entry out into
     plain JS objects — and drops the vector without `.delete()`, so the C++
     side of it stays on the wasm heap: 16–18 B per EMPTY decode, and a hit
     leaks its text and bytes. Measured on the vendored build: 20 000 decodes
     of a 2000-byte QR grew the engine from 21.3 to 53.0 MB; with the delete,
     flat at 21.3 MB. A live camera decodes 3 frames a second for as long as
     the page is open, and /optical-transfer 30–60.
     The vendored file stays untouched: the module's two read entry points are
     wrapped once, and each returned vector is deleted on the next microtask —
     i.e. right after the wrapper has finished copying it (3.1.0 reads it
     synchronously after the call; the copies are independent of the heap,
     checked: `bytes` survives heap churn byte for byte). The same wrapper
     lives in optical-transfer/decode-worker.js, which cannot load this file. */
  function later(fn) {
    if (typeof queueMicrotask === 'function') queueMicrotask(fn);
    else Promise.resolve().then(fn);
  }
  function freeResults(mod) {
    if (!mod || mod.__qrFreesResults) return mod;
    ['readBarcodesFromPixmap', 'readBarcodesFromImage'].forEach(function (name) {
      var orig = mod[name];
      if (typeof orig !== 'function') return;
      mod[name] = function () {
        var v = orig.apply(this, arguments);
        if (v && typeof v.delete === 'function') later(function () { try { v.delete(); } catch (e) {} });
        return v;
      };
    });
    mod.__qrFreesResults = true;
    return mod;
  }

  function msgOf(e) { return String((e && (e.message || e)) || ''); }
  function typed(cause, code, text) {
    var e = new Error(text + (cause ? ' (' + msgOf(cause).slice(0, 160) + ')' : ''));
    e.code = code;
    e.cause = cause;
    return e;
  }
  // Chrome: "Array buffer allocation failed"; Firefox: "out of memory";
  // zxing-wasm: "Failed to allocate N bytes in WASM memory"; emscripten:
  // "Cannot enlarge memory" / "Aborted(OOM)".
  function isMemoryError(e) {
    if (!e) return false;
    if (e.code === 'MEMORY') return true;
    return /out of memory|Array buffer allocation failed|Failed to allocate|allocation failed|Cannot enlarge memory|\(OOM\)/i.test(msgOf(e));
  }
  // A trap or an abort inside the wasm leaves the module unusable.
  function isEngineDeath(e) {
    if (!e) return false;
    if (e.code === 'ENGINE') return true;
    return (typeof WebAssembly !== 'undefined' && WebAssembly.RuntimeError && e instanceof WebAssembly.RuntimeError)
      || /Aborted\(/.test(msgOf(e));
  }
  function dropEngine() {
    try { if (Z && Z.purgeZXingModule) Z.purgeZXingModule(); } catch (e) {}
    readyPromise = null;
  }
  /* What a decode that THREW means. A throw is never "no code": a dead engine
     and an image that does not fit in memory leave the ladder with their
     type; anything else (an input the engine does not understand) is still a
     miss for that rung, as it always was. */
  function rethrowIfFatal(e) {
    if (isMemoryError(e)) {
      if (isEngineDeath(e)) dropEngine();
      throw e && e.code === 'MEMORY' ? e : typed(e, 'MEMORY', 'qr-decode: the image does not fit in memory');
    }
    if (isEngineDeath(e)) {
      dropEngine();
      throw e && e.code === 'ENGINE' ? e : typed(e, 'ENGINE', 'qr-decode: the decoder engine stopped');
    }
  }
  function reportFail(err) {
    if (failReported) return;
    failReported = true;
    try { w.__aeDiagnostic && w.__aeDiagnostic({ issue: 'qr_engine_failed', err: msgOf(err.cause || err).slice(0, 120) }); } catch (e) {}
  }

  // ZXing-C++ → html5-qrcode format names, so barcode-scanner's FORMAT_INFO /
  // __BarcodeParsers keep receiving the names they already branch on.
  //
  // ⚠️ Micro QR and rMQR get their OWN names — they are NOT folded into QR_CODE.
  // Until 2026-09-09 they were, and that one word cost three things at once:
  // the result card said "QR Code" for a symbol that is not one, the history
  // and CSV export recorded the wrong symbology, and `tool_complete.format` —
  // our only instrument — could not count them at all (30 days of RU traffic:
  // 24 443 QR_CODE completions with Micro QR invisible inside that bucket).
  // Measured the same day on 186 degraded renders: Micro QR recall 99%
  // (123/124) against 0/64 for jsQR, html5-qrcode and the native
  // BarcodeDetector. It is our widest decode differentiator, and it survived
  // only as an unnamed side effect of passing no format filter — a user had to
  // write in to tell us it was there. It gets to say its name.
  var FMT = {
    QRCode: 'QR_CODE', MicroQRCode: 'MICRO_QR_CODE', RMQRCode: 'RMQR_CODE',
    DataMatrix: 'DATA_MATRIX', Aztec: 'AZTEC', PDF417: 'PDF_417', MaxiCode: 'MAXICODE',
    EAN13: 'EAN_13', EAN8: 'EAN_8', UPCA: 'UPC_A', UPCE: 'UPC_E',
    Code128: 'CODE_128', Code39: 'CODE_39', Code93: 'CODE_93', Codabar: 'CODABAR',
    ITF: 'ITF', MicroPDF417: 'MICRO_PDF_417', DXFilmEdge: 'DX_FILM_EDGE',
    DataBar: 'DATA_BAR', DataBarExpanded: 'DATA_BAR_EXPANDED',
    DataBarLimited: 'DATA_BAR_LIMITED', DataBarStk: 'DATA_BAR_STACKED',
    DataBarOmni: 'DATA_BAR_OMNI', DataBarStkOmni: 'DATA_BAR_STACKED_OMNI',
    DataBarExpStk: 'DATA_BAR_EXPANDED_STACKED',
  };

  // An unmapped symbology still DECODES — it just reaches the card as a raw
  // enum ("DATABARSTK"). Three did, for 90 days: DataBar Stacked 466 scans
  // (more than Aztec, ITF and PDF417 combined), MicroPDF417 70, DX Film Edge
  // 24. They are in the map now; their telemetry buckets read DATABARSTK /
  // MICROPDF417 / DXFILMEDGE in anything older than 2026-09-09.
  //
  // The list above cannot be closed by inspection: zxing-wasm reports the FINE
  // symbology in `format` (MicroQRCode, DataBarStk) and only the coarse family
  // in `symbology`, and the build's table carries ~50 fine names. So rather
  // than pretend, the gap REPORTS ITSELF — the next unmapped symbology says so
  // on its first scan, instead of waiting for someone to read a 90-day
  // histogram, which is how DataBar Stacked went 466 scans unnamed.
  var reportedGaps = {};
  function normFormat(f) {
    if (FMT[f]) return FMT[f];
    if (!f) return 'UNKNOWN';
    var raw = String(f);
    if (!reportedGaps[raw]) {
      reportedGaps[raw] = 1;
      try { w.__aeDiagnostic && w.__aeDiagnostic({ issue: 'qr_format_unmapped', fmt: raw }); } catch (e) {}
    }
    return raw.toUpperCase();
  }

  // ── Format FILTERS ───────────────────────────────────────────────────────
  // The map above translates ZXing→ours on the way OUT. Callers naturally feed
  // those same names back IN (`formats: ['QR_CODE', 'DATA_MATRIX']` — that is
  // exactly what boarding-pass does), so the reverse direction has to exist
  // too. It survived only because zxing-wasm happens to parse 'QR_CODE'
  // leniently; 'DATA_BAR' or a typo does not parse, and the failure is SILENT —
  // measured 2026-09-09: `formats: ['NOT_A_FORMAT']` returns zero hits and
  // throws nothing, i.e. a one-word mistake turns the decoder off and looks
  // exactly like "no code in this image". So: accept either spelling, and make
  // an unknown name loud.
  var UNFMT = { MICRO_QR_CODE: 'MicroQRCode', RMQR_CODE: 'RMQRCode', MAXICODE: 'MaxiCode' };
  (function () {
    for (var zx in FMT) if (Object.prototype.hasOwnProperty.call(FMT, zx)) {
      if (!UNFMT[FMT[zx]]) UNFMT[FMT[zx]] = zx;   // QR_CODE → QRCode, …
      UNFMT[zx.toUpperCase()] = zx;               // 'QRCODE' → QRCode
    }
  })();
  function canonFormats(list) {
    if (!list || !list.length) return [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var raw = String(list[i]);
      var hit = UNFMT[raw] || UNFMT[raw.toUpperCase()] || (FMT[raw] ? raw : null);
      if (!hit) {
        throw new Error('qr-decode: unknown barcode format "' + raw + '" — a filter ' +
          'the decoder cannot resolve silently matches nothing. Known: ' +
          Object.keys(FMT).join(', '));
      }
      if (out.indexOf(hit) < 0) out.push(hit);
    }
    return out;
  }

  // Reader options. textMode 'Plain' is deliberate: the library default 'HRI'
  // rewrites control chars (FNC1/GS/CR) into literal "<GS>" placeholders, which
  // corrupts GS1 ("Честный ЗНАК"), MaxiCode and vCard payloads our parsers read.
  function baseOpts(opts) {
    opts = opts || {};
    return {
      formats: canonFormats(opts.formats),
      textMode: 'Plain',
      tryHarder: true,
      tryInvert: true,          // white-on-black codes — the classic silent miss
      tryRotate: true,
      tryDownscale: true,
      maxNumberOfSymbols: opts.maxSymbols || 16,
      binarizer: 'LocalAverage',
      returnErrors: false,
    };
  }

  w.__qrDecode = {
    isSupported: function () {
      return typeof w.WebAssembly !== 'undefined' &&
        typeof w.createImageBitmap === 'function';
    },

    ready: function () {
      if (readyPromise) return readyPromise;
      // A start that just failed is not retried on every call: a camera loop
      // asks 3 times a second, and each attempt refetches a 1 MB wasm. Until
      // the back-off passes, the same typed failure answers at once; retry()
      // (a person pressing "Try again") skips the wait.
      if (lastFail && Date.now() - lastFail.at < backoffMs(lastFail.n)) return Promise.reject(lastFail.err);
      var attempt = new Promise(function (resolve, reject) {
        if (!w.__qrDecode.isSupported()) {
          var u = new Error('wasm_unsupported'); u.code = 'UNSUPPORTED'; reject(u); return;
        }
        function fail(cause) {
          // zxing-wasm caches the module PROMISE — a rejected one included —
          // and hands it to every later decode. Dropping it is what makes the
          // next start a real start; boot() reapplies the self-hosted location
          // (a purged module otherwise falls back to the library's CDN).
          dropEngine();
          reject(typed(cause, 'ENGINE', 'qr-decode: the decoder engine did not start'));
        }
        function boot() {
          try {
            Z = w.ZXingWASM;
            // Self-hosted wasm — never let the lib fetch from a CDN.
            Z.setZXingModuleOverrides({
              locateFile: function (path, prefix) {
                return path.slice(-5) === '.wasm' ? WASM_URL : prefix + path;
              },
            });
            // Warm the module so the first real decode isn't a cold-start.
            // prepareZXingModule may return a promise OR undefined depending on
            // args — guard both so a non-thenable never blows up ready().
            var warm = Z.prepareZXingModule && Z.prepareZXingModule({ fireImmediately: true });
            if (warm && typeof warm.then === 'function') warm.then(function (mod) { freeResults(mod); resolve(Z); }, fail);
            else resolve(Z);
          } catch (e) { fail(e); }
        }
        if (w.ZXingWASM) { boot(); return; }
        // Fallback if a page forgot the static <script> tag: inject the reader
        // IIFE on demand. Under CSP3 strict-dynamic a nonced script's injected
        // child is trusted automatically; under the CSP2 fallback `'self'`
        // covers this same-origin src. So no explicit nonce is needed.
        var s = document.createElement('script');
        s.src = '/vendor/zxing/zxing-reader.iife.js';
        s.onload = boot;
        s.onerror = function () { fail(new Error('zxing_script_load_failed')); };
        document.head.appendChild(s);
      });
      readyPromise = attempt;
      attempt.then(function () { lastFail = null; }, function (err) {
        if (readyPromise === attempt) readyPromise = null;
        lastFail = { at: Date.now(), n: (lastFail ? lastFail.n : 0) + 1, err: err };
        if (err && err.code === 'ENGINE') reportFail(err);
      });
      return attempt;
    },

    // "Try again" from a person: start the engine now, past the back-off.
    retry: function () {
      lastFail = null;
      return this.ready();
    },

    // ── Uploads / paste: full ladder (whole → tiles → upscale) ──
    fromFile: function (file, opts) {
      return this.ready().then(function () {
        return ladder(file, opts);
      }).then(settleText);
    },
    fromImageData: function (imageData, opts) {
      return this.ready().then(function () {
        return ladder(imageData, opts);
      }).then(settleText);
    },
    fromBitmap: function (bitmap, opts) {
      return this.ready().then(function () {
        return ladder(bitmap, opts);
      }).then(settleText);
    },

    // ── Live camera frames: ONE fast pass, no ladder (frames are cheap and
    // continuous — spending 400ms tiling each frame would tank the FPS). ──
    fromCanvasFast: function (canvas, opts) {
      return this.ready().then(function () {
        var id = canvas.getContext('2d', { willReadFrequently: true })
          .getImageData(0, 0, canvas.width, canvas.height);
        var ro = baseOpts(opts);
        return decodeImageData(id, ro).then(mapResults, function (e) {
          rethrowIfFatal(e);
          throw e;
        }).then(function (list) { return reproduceOnCanvas(list, canvas, ro); });
      }).then(confirmLive).then(settleText);
    },

    // ── The live loop's input: a camera frame cut for the decoder (see
    // liveFrame below). Sync, no engine needed — the loop decodes the canvas
    // with fromCanvasFast. ──
    liveFrame: function (video, opts) {
      return liveFrame(video, opts || {});
    },
    liveCameraSize: function () {
      return liveCameraSize();
    },

    // Pixels of an image FILE as a bitmap, SVG included (see imageBitmap
    // below), at most `maxSide` on the long side. For callers that show the
    // picture back — they do not need to keep 48 MP around to draw 860 px.
    imageBitmap: function (blob, opts) {
      return imageBitmap(blob, opts);
    },
    isMemoryError: isMemoryError,
    // for tests (tests/js/qr-decode-text-encoding.test.mjs)
    _settleText: function (list) { return Promise.resolve(settleText(list)); },
    // for instruments and tests: a new scene — the live memory of weak reads starts empty
    _forgetLive: function () { liveSeen = []; },
    _confirmLive: function (list) { return confirmLive(list); },
  };

  function backoffMs(n) { return n <= 1 ? 3000 : (n === 2 ? 10000 : 30000); }

  /* ── A weak symbology must read twice before it counts (2026-10-29) ──────
     QR, Data Matrix, Aztec, PDF417, MaxiCode carry Reed–Solomon codewords;
     EAN/UPC and Code 128 a check digit the reader verifies. GS1 DataBar has a
     mod-79 check over a few wide elements, MicroPDF417 here, DX Film Edge,
     Code 39 / 93, Codabar and ITF little or none — and with tryHarder,
     tryRotate and tryDownscale the engine tries thousands of rows of every
     picture, so a texture (fabric, a keyboard, a striped wall, text lines)
     comes back as one now and then. /qr-scanner, RU, 14 days to 29.10.2026:
     «GS1 DataBar Stacked» was the result in 494 sessions; after it, from the
     camera, a code of ANOTHER format was read within two minutes 35 % of the
     time (after a QR — 0.8 %): the camera stopped on a false read, the card
     showed a made-up product number, and the person scanned on. The same
     profile for MicroPDF417 (35 %), Code 39 and DX Film Edge (22 %).
       A real code reads again; a false one does not. Live — the same text of
     the same symbology on another frame within LIVE_CONFIRM_MS (a code held
     in front of the camera is read on frame after frame; the loop ticks every
     300 ms). Upload — the same text again from the spot it was found at,
     cut out and read at another scale (confirmWeakHits in the ladder).
     Measured by tests/playwright/qr-decode-false-reads-probe.mjs. */
  var WEAK = {
    DATA_BAR: 1, DATA_BAR_EXPANDED: 1, DATA_BAR_LIMITED: 1, DATA_BAR_STACKED: 1, DATA_BAR_OMNI: 1,
    DATA_BAR_STACKED_OMNI: 1, DATA_BAR_EXPANDED_STACKED: 1, MICRO_PDF_417: 1, DX_FILM_EDGE: 1,
    CODE_39: 1, CODE_93: 1, CODABAR: 1, ITF: 1,
  };
  var LIVE_CONFIRM_MS = 2000;
  var liveSeen = [];   // { key, at } — weak reads of the last LIVE_CONFIRM_MS
  function isWeak(r) { return !!(r && WEAK[r.format]); }
  function confirmLive(list) {
    if (!list || !list.length) return list;
    var now = Date.now(), out = [], i, j;
    liveSeen = liveSeen.filter(function (x) { return now - x.at < LIVE_CONFIRM_MS; });
    for (i = 0; i < list.length; i++) {
      var r = list[i];
      if (!isWeak(r)) { out.push(r); continue; }
      var key = r.format + '\n' + r.text, seen = false;
      for (j = 0; j < liveSeen.length; j++) if (liveSeen[j].key === key) { seen = true; break; }
      liveSeen.push({ key: key, at: now });
      if (seen) out.push(r);
    }
    return out;
  }
  /* The same check the upload ladder makes (confirmWeakHits): the spot a weak
     hit came from, cut out of the frame and read at another scale, must give
     the same text. A texture that fooled one pass of rows rarely survives
     another sampling of the same pixels; a printed symbol always does. On a
     camera that is one or two extra decodes of a small crop, only on a weak hit. */
  var REPRODUCE_SCALES = [1.5, 0.75];
  function reproduceOnCanvas(list, canvas, ro) {
    if (!list || !list.some(isWeak)) return list;
    var keep = [];
    return list.reduce(function (p, r) {
      return p.then(function () {
        if (!isWeak(r)) { keep.push(r); return; }
        return reproduces(r, function (sx, sy, sw, sh, sc) {
          var cv = document.createElement('canvas');
          cv.width = Math.max(1, Math.round(sw * sc)); cv.height = Math.max(1, Math.round(sh * sc));
          var cx = cv.getContext('2d', { willReadFrequently: true });
          cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = 'high';
          cx.drawImage(canvas, sx, sy, sw, sh, 0, 0, cv.width, cv.height);
          var id = cx.getImageData(0, 0, cv.width, cv.height);
          cv.width = cv.height = 0;
          return id;
        }, canvas.width, canvas.height, ro).then(function (ok) { if (ok) keep.push(r); });
      });
    }, Promise.resolve()).then(function () { return keep; });
  }
  // cut(sx, sy, sw, sh, scale) -> ImageData of that spot; W×H — the picture's size
  function reproduces(r, cut, W, H, ro) {
    var b = boxOf(r.position);
    if (!b) return Promise.resolve(false);
    var m = Math.max(b.w, b.h) * 0.5 + 24;
    var x0 = Math.max(0, b.x - m), y0 = Math.max(0, b.y - m);
    var x1 = Math.min(W, b.x + b.w + m), y1 = Math.min(H, b.y + b.h + m);
    if (x1 - x0 < 8 || y1 - y0 < 8) return Promise.resolve(false);
    var o = Object.assign({}, ro, { formats: canonFormats([r.format]) });
    var k = 0;
    function next() {
      if (k >= REPRODUCE_SCALES.length) return Promise.resolve(false);
      var sc = Math.min(REPRODUCE_SCALES[k++], RESCUE_SIDE / Math.max(x1 - x0, y1 - y0));
      return decodeImageData(cut(x0, y0, x1 - x0, y1 - y0, sc), o).then(mapResults, function (e) { rethrowIfFatal(e); return []; })
        .then(function (got) {
          for (var z = 0; z < got.length; z++) if (got[z].text === r.text) return true;
          return next();
        });
    }
    return next();
  }
  function boxOf(p) {
    if (!p) return null;
    var xs = [], ys = [];
    for (var k in p) {
      if (!Object.prototype.hasOwnProperty.call(p, k)) continue;
      var q = p[k];
      if (q && typeof q.x === 'number' && typeof q.y === 'number') { xs.push(q.x); ys.push(q.y); }
    }
    if (!xs.length) return null;
    var x0 = Math.min.apply(null, xs), y0 = Math.min.apply(null, ys);
    return { x: x0, y: y0, w: Math.max.apply(null, xs) - x0, h: Math.max.apply(null, ys) - y0 };
  }

  // ── Live camera frames: the pixels of the CODE, not of the frame ─────────
  /* A small code («Честный знак» DataMatrix, 20×20 or 36×36 modules a few mm
     wide) is unreadable not because of the engine but because of how many
     camera pixels reach it. Measured 2026-10-08 on rendered camera frames (code
     width as a share of the frame width, 16 frames a cell, rotation, blur,
     noise, print contrast): a 720p stream scaled whole to 1000 px read a 36×36
     DataMatrix from 17–20 % of the width, 20×20 from 9 %, QR v3 from 12 %; a
     1080p stream with the scan region at its own pixels — 10 % / 6 % / 8 %. A
     whole 1080p frame at its own pixels reads as well as the region but costs
     three times as much (≈210 ms against 72–84 on a desktop).
     So the ticks take turns. The SCAN REGION — the aim element's box plus a
     tenth around it, or without an aim the centred square where a person holds
     a code (74 % of the visible short side) — at the stream's own pixels; and
     the WHOLE FRAME scaled to ≤ 1000 px, where a big or off-centre code reads
     as it always did. Every live scanner takes its frames here (/qr-scanner,
     the ticket door scanner, the /qr-code scanner tab) — until 2026-10-08 each
     had its own copy of the whole-frame-only loop. */
  var LIVE_FULL_MAX = 1000, LIVE_REGION_MAX = 1200, LIVE_MARGIN = 0.1, LIVE_DEFAULT_AIM = 0.74;
  var liveStates = typeof WeakMap === 'function' ? new WeakMap() : null;

  /* What a live loop asks the camera for. A phone: 1080p — in the region pass
     every stream pixel is a code pixel. A desktop webcam keeps 720p: a cheap
     USB one drops to a few frames a second at 1080p, and codes held up to a
     laptop are mostly on screens. */
  function liveCameraSize() {
    var touch = false;
    try { touch = (navigator.maxTouchPoints || 0) > 0 && w.matchMedia('(pointer:coarse)').matches; } catch (e) {}
    return touch ? { width: 1920, height: 1080 } : { width: 1280, height: 720 };
  }

  /* The scan region in VIDEO pixels: the element's own object-fit mapping
     (cover / contain / fill) run backwards from the aim box on screen. Null
     when the element is not laid out or the region is too small to matter. */
  function liveRegion(video, aim, margin) {
    var vw = video.videoWidth, vh = video.videoHeight;
    var R = video.getBoundingClientRect();
    if (!R.width || !R.height) return null;
    var fit = 'contain';
    try { fit = w.getComputedStyle(video).objectFit || 'contain'; } catch (e) {}
    var kx, ky;
    if (fit === 'fill') { kx = R.width / vw; ky = R.height / vh; }
    else { kx = ky = (fit === 'cover' ? Math.max : Math.min)(R.width / vw, R.height / vh); }
    var dx = (R.width - vw * kx) / 2, dy = (R.height - vh * ky) / 2;
    var ax, ay, aw, ah, A = aim && aim.getBoundingClientRect ? aim.getBoundingClientRect() : null;
    if (A && A.width > 0 && A.height > 0) {
      ax = A.left - R.left; ay = A.top - R.top; aw = A.width; ah = A.height;
    } else {
      // the visible picture: the content box clipped by the element's box
      var lx = Math.max(0, dx), ly = Math.max(0, dy);
      var rw = Math.min(R.width, dx + vw * kx) - lx, rh = Math.min(R.height, dy + vh * ky) - ly;
      if (rw <= 0 || rh <= 0) return null;
      aw = ah = LIVE_DEFAULT_AIM * Math.min(rw, rh);
      ax = lx + (rw - aw) / 2; ay = ly + (rh - ah) / 2;
    }
    var m = aw * margin;
    var x0 = Math.max(0, (ax - m - dx) / kx), y0 = Math.max(0, (ay - m - dy) / ky);
    var x1 = Math.min(vw, (ax + aw + m - dx) / kx), y1 = Math.min(vh, (ay + ah + m - dy) / ky);
    if (x1 - x0 < 64 || y1 - y0 < 64) return null;
    return { x: Math.round(x0), y: Math.round(y0), w: Math.round(x1 - x0), h: Math.round(y1 - y0) };
  }

  // One canvas per pass: the two differ in size, and a canvas resized every
  // tick reallocates its pixels every tick.
  function liveSurface(st, pass, cw, ch) {
    var s = st.surfaces[pass];
    if (!s) {
      var c = document.createElement('canvas');
      s = st.surfaces[pass] = { canvas: c, ctx: c.getContext('2d', { willReadFrequently: true }) };
    }
    if (s.canvas.width !== cw) s.canvas.width = cw;
    if (s.canvas.height !== ch) s.canvas.height = ch;
    return s;
  }

  /* The system can take the camera's frame away between any check and the
     draw (iOS: App Switcher, Control Center, a call — the track goes muted;
     WebKit then throws InvalidStateError from drawImage, Sentry J4). The
     shared frame draw answers false instead; a loop that set its "busy" flag
     before such a throw stayed busy — and blind — for good. */
  function liveDraw(ctx, video, sx, sy, sw, sh, dw, dh) {
    var CM = w.CameraManager;
    if (CM && typeof CM.drawFrame === 'function') return CM.drawFrame(ctx, video, sx, sy, sw, sh, 0, 0, dw, dh);
    try { ctx.drawImage(video, sx, sy, sw, sh, 0, 0, dw, dh); return true; }
    catch (e) { if (e && (e.name === 'InvalidStateError' || e.code === 11)) return false; throw e; }
  }

  /* video: a playing <video>. opts: { aim: element, state: object kept per
     camera session (else one per video element), margin }. Returns
     { canvas, map } — map.pass is 'roi' or 'full', and a point the decoder
     reports maps back to the video as x = map.x + p.x / map.s — or null when
     there is no frame to take this tick. */
  function liveFrame(video, opts) {
    if (!video || !(video.videoWidth > 0) || video.readyState < 2) return null;
    var st = opts.state || (liveStates && liveStates.get(video));
    if (!st) { st = {}; if (liveStates && !opts.state) liveStates.set(video, st); }
    if (!st.surfaces) { st.surfaces = {}; st.tick = 0; }
    var vw = video.videoWidth, vh = video.videoHeight;
    var region = (st.tick++ % 2 === 0) ? liveRegion(video, opts.aim, opts.margin == null ? LIVE_MARGIN : opts.margin) : null;
    var pass, sx, sy, sw, sh, s;
    if (region) {
      pass = 'roi'; sx = region.x; sy = region.y; sw = region.w; sh = region.h;
      s = Math.min(1, LIVE_REGION_MAX / Math.max(sw, sh));
    } else {
      pass = 'full'; sx = 0; sy = 0; sw = vw; sh = vh;
      s = Math.min(1, LIVE_FULL_MAX / Math.max(vw, vh));
    }
    var surf = liveSurface(st, pass, Math.max(1, Math.round(sw * s)), Math.max(1, Math.round(sh * s)));
    if (!liveDraw(surf.ctx, video, sx, sy, sw, sh, surf.canvas.width, surf.canvas.height)) return null;
    return { canvas: surf.canvas, map: { x: sx, y: sy, s: s, pass: pass } };
  }

  // ── Core decode helpers ──────────────────────────────────────────────────
  function decodeBlob(blob, ro) {
    return Z.readBarcodes(blob, ro);
  }
  function decodeImageData(id, ro) {
    return Z.readBarcodesFromImageData ? Z.readBarcodesFromImageData(id, ro) : Z.readBarcodes(id, ro);
  }

  // `map` says where the pixels the engine saw came from: a scaled whole frame
  // or a tile reports positions in ITS coordinates, and the caller gets them in
  // the source image's.
  function mapResults(raw, map) {
    if (!raw || !raw.length) return [];
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var r = raw[i];
      // zxing-wasm returns a one-element array with an EMPTY text / "None"
      // format for a no-symbol image instead of an empty array. Treating that
      // as a hit is a false positive (e.g. a blank photo reads as "success"
      // with empty content). A real barcode always carries non-empty text.
      if (!r || typeof r.text !== 'string' || r.text.length === 0) continue;
      if (r.format === 'None' || r.format === 'NONE') continue;
      out.push({
        text: r.text,
        format: normFormat(r.format),
        bytes: r.bytes || null,
        position: mapPosition(r.position, map),
        gs1: r.contentType === 'GS1',
        sym: typeof r.symbologyIdentifier === 'string' ? r.symbologyIdentifier : '',
        eci: !!r.hasECI,
        // ']d2' for a GS1 DataMatrix (FNC1 first), ']d1' for a plain one. The
        // text of both can be byte-identical — a marking code printed WITHOUT
        // FNC1 is the commonest ЧЗ print defect, and only this says so.
        symbology: r.symbologyIdentifier || null,
        contentType: r.contentType || null,
      });
    }
    return out;
  }
  /* ── The text the bytes really carry (2026-10-21, review #1960) ────────────
     A QR / DataMatrix byte segment without an ECI declares no encoding, and
     ZXing guesses UTF-8, Shift_JIS or ISO-8859-1. Russian payment QRs
     (ГОСТ Р 56042, header «ST00011» = Windows-1251) and labels printed by
     Russian accounting software are Windows-1251, so the card, the history and
     the CSV said «ÎÎÎ «Ðîìàøêà»» (stand: a 1251 payment QR and a 1251 text QR,
     both mojibake on prod 2026-10-21). A payload that names its encoding — the
     ГОСТ header digit — is read in it. Any other non-UTF-8 payload with high
     bytes asks the one owner of «which encoding is this text», TagTextEncoding
     (public/js/tag-text-encoding.js — 46 KB of models, so it loads only when
     such a payload appears); unsure means the text stays as ZXing read it. */
  var GOST_ENC = { '1': 'windows-1251', '3': 'koi8-r' };
  var tteP = null;
  function tagTextEncoding() {
    if (w.TagTextEncoding) return Promise.resolve(w.TagTextEncoding);
    if (!tteP) {
      tteP = new Promise(function (resolve) {
        var s = document.createElement('script');
        s.src = typeof w.__jsSrc === 'function' ? w.__jsSrc('/js/tag-text-encoding.js') : '/js/tag-text-encoding.js';
        s.onload = function () { resolve(w.TagTextEncoding || null); };
        s.onerror = function () { tteP = null; resolve(null); };
        document.head.appendChild(s);
      });
    }
    return tteP;
  }
  function hasHighBytes(b) { for (var i = 0; i < b.length; i++) if (b[i] >= 0x80) return true; return false; }
  function isUtf8(b) { try { new TextDecoder('utf-8', { fatal: true }).decode(b); return true; } catch (e) { return false; } }
  function settleText(list) {
    if (!list || !list.length || typeof TextDecoder === 'undefined') return list;
    var pending = [];
    for (var i = 0; i < list.length; i++) {
      var r = list[i], b = r.bytes;
      if (!b || !b.length || r.eci || !hasHighBytes(b) || isUtf8(b)) continue;
      var gost = /^ST0001([13])\|/.exec(r.text);
      if (gost) {
        try { r.rawText = r.text; r.text = new TextDecoder(GOST_ENC[gost[1]]).decode(b); r.enc = GOST_ENC[gost[1]]; continue; }
        catch (e) { r.text = r.rawText; delete r.rawText; }
      }
      pending.push(r);
    }
    if (!pending.length) return list;
    return tagTextEncoding().then(function (T) {
      if (!T) return list;
      var loc = String(w.__LOCALE || 'en');
      pending.forEach(function (r) {
        try {
          var g = T.guess([r.bytes], { locale: loc });
          if (g && g.enc && g.auto) { r.rawText = r.text; r.text = T.decode(r.bytes, g.enc); r.enc = g.enc; }
        } catch (e) {}
      });
      return list;
    });
  }

  function mapPosition(p, map) {
    if (!p) return null;
    if (!map || (map.s === 1 && !map.x && !map.y)) return p;
    var out = {};
    for (var k in p) {
      if (!Object.prototype.hasOwnProperty.call(p, k)) continue;
      var q = p[k];
      out[k] = (q && typeof q.x === 'number') ? { x: map.x + q.x / map.s, y: map.y + q.y / map.s } : q;
    }
    return out;
  }
  function dedupe(list) {
    var seen = {}, out = [];
    for (var i = 0; i < list.length; i++) {
      var t = list[i].text;
      if (t == null || seen[t]) continue;
      seen[t] = 1; out.push(list[i]);
    }
    return out;
  }

  // ── What the file IS, from its first bytes ─────────────────────────────────
  // The engine decodes JPEG, PNG, GIF and BMP itself (stb_image inside the
  // wasm) — nothing else. WebP, AVIF, HEIC-in-Safari and SVG came back from
  // it as "no code" without being looked at, so their first whole-image pass
  // never happened: the ladder started them on 2×2 tiles. They now get their
  // pixels from the browser, like everything above FIRST_PASS_MAX_SIDE.
  var HEAD_BYTES = 262144;
  function u16be(b, i) { return (b[i] << 8) | b[i + 1]; }
  function u32be(b, i) { return ((b[i] << 24) >>> 0) + ((b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]); }
  function u16le(b, i) { return b[i] | (b[i + 1] << 8); }
  function i32le(b, i) { return b[i] | (b[i + 1] << 8) | (b[i + 2] << 16) | (b[i + 3] << 24); }
  function sniffHead(b, type, name) {
    var n = b.length;
    if (n >= 24 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) {
      return { kind: 'png', w: u32be(b, 16), h: u32be(b, 20), alpha: pngHasAlpha(b) };
    }
    if (n >= 10 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38) {
      return { kind: 'gif', w: u16le(b, 6), h: u16le(b, 8) };
    }
    if (n >= 26 && b[0] === 0x42 && b[1] === 0x4D) {
      return { kind: 'bmp', w: Math.abs(i32le(b, 18)), h: Math.abs(i32le(b, 22)) };
    }
    if (n >= 4 && b[0] === 0xFF && b[1] === 0xD8) {
      // JPEG: walk the segments to the frame header (SOFn). EXIF and ICC
      // segments can push it far in; not found in the head = size unknown.
      var i = 2;
      while (i + 9 < n) {
        if (b[i] !== 0xFF) { i++; continue; }
        var m = b[i + 1];
        if (m === 0xFF) { i++; continue; }
        if (m === 0xD8 || m === 0x01 || (m >= 0xD0 && m <= 0xD7)) { i += 2; continue; }
        if (m >= 0xC0 && m <= 0xCF && m !== 0xC4 && m !== 0xC8 && m !== 0xCC) {
          return { kind: 'jpeg', w: u16be(b, i + 7), h: u16be(b, i + 5) };
        }
        if (m === 0xDA || m === 0xD9) break;   // scan data before any frame header
        i += 2 + u16be(b, i + 2);
      }
      return { kind: 'jpeg', w: 0, h: 0 };
    }
    var t = String(type || '').toLowerCase();
    if (t === 'image/svg+xml' || /\.svg$/i.test(String(name || '')) || looksLikeSvg(b)) return { kind: 'svg', w: 0, h: 0 };
    return { kind: 'other', w: 0, h: 0 };
  }
  /* A PNG that can be see-through: colour type 4/6 (grey/RGB + alpha) or a
     tRNS chunk before the image data. A code exported from a generator is often
     dark modules on NOTHING — and the engine reads a transparent pixel by its
     colour, (0,0,0): black. The whole picture was one black square, «no code»
     (QA 23.10.2026, /marking-check: a bwip-js DataMatrix PNG). Such a picture
     goes the pixel way, laid on white paper first. */
  function pngHasAlpha(b) {
    var ct = b[25];
    if (ct === 4 || ct === 6) return true;
    var i = 8;
    while (i + 8 <= b.length) {
      var len = u32be(b, i), type = String.fromCharCode(b[i + 4], b[i + 5], b[i + 6], b[i + 7]);
      if (type === 'tRNS') return true;
      if (type === 'IDAT' || type === 'IEND') return false;
      i += 12 + len;
    }
    return false;
  }
  function looksLikeSvg(b) {
    var s = '';
    for (var i = 0; i < Math.min(b.length, 2048); i++) s += String.fromCharCode(b[i]);
    return /<svg[\s>]/i.test(s) && /^\s*(\uFEFF|\xEF\xBB\xBF)?\s*</.test(s);
  }
  function sniff(blob) {
    return blob.slice(0, HEAD_BYTES).arrayBuffer().then(function (ab) {
      return sniffHead(new Uint8Array(ab), blob.type, blob.name);
    }, function () { return { kind: 'other', w: 0, h: 0 }; });
  }
  var ENGINE_FORMATS = { jpeg: 1, png: 1, gif: 1, bmp: 1 };

  // ── Pixels from the browser ────────────────────────────────────────────────
  // An SVG is a picture with no pixels: createImageBitmap refuses it in both
  // Chromium and Firefox (measured 26.09 — every SVG picked on /qr-scanner in
  // 14 days, 33 of them, got "No code found"). It is rendered by an <img>
  // instead, on WHITE — a QR exported as SVG is dark modules on nothing, and
  // "nothing" reads as black. A root without width/height gets them from its
  // viewBox: an SVG with no intrinsic size cannot be drawn to a canvas in
  // every browser.
  function rasterizeSvg(blob) {
    return blob.text().then(function (text) {
      var m = /<svg\b[^>]*>/i.exec(text);
      if (!m) throw new Error('svg_unreadable');
      var tag = m[0];
      var attr = function (a) { var r = new RegExp('\\s' + a + '\\s*=\\s*["\']([^"\']*)["\']', 'i').exec(tag); return r ? r[1] : null; };
      var absolute = function (v) { return v != null && /^\s*[\d.]+\s*(px|pt|pc|mm|cm|in)?\s*$/i.test(v); };
      if (!absolute(attr('width')) || !absolute(attr('height'))) {
        var vb = (attr('viewBox') || '').trim().split(/[\s,]+/).map(Number);
        var vw = vb.length === 4 && vb[2] > 0 ? vb[2] : SVG_MIN_SIDE, vh = vb.length === 4 && vb[3] > 0 ? vb[3] : SVG_MIN_SIDE;
        var k = SVG_MIN_SIDE / Math.max(vw, vh);
        var fixed = tag.replace(/\s(width|height)\s*=\s*["'][^"']*["']/gi, '')
          .replace(/^<svg\b/i, '<svg width="' + Math.round(vw * k) + '" height="' + Math.round(vh * k) + '"');
        text = text.slice(0, m.index) + fixed + text.slice(m.index + tag.length);
      }
      return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(new Blob([text], { type: 'image/svg+xml' }));
        var img = new Image();
        img.onload = function () {
          var cv = document.createElement('canvas');
          try {
            var nw = img.naturalWidth || SVG_MIN_SIDE, nh = img.naturalHeight || SVG_MIN_SIDE;
            var long = Math.max(nw, nh);
            var s = Math.min(SVG_MAX_SIDE, Math.max(SVG_MIN_SIDE, long)) / long;
            cv.width = Math.max(1, Math.round(nw * s));
            cv.height = Math.max(1, Math.round(nh * s));
            var cx = cv.getContext('2d');
            cx.fillStyle = '#fff';
            cx.fillRect(0, 0, cv.width, cv.height);
            cx.drawImage(img, 0, 0, cv.width, cv.height);
            URL.revokeObjectURL(url);
            createImageBitmap(cv).then(function (bmp) { cv.width = cv.height = 0; resolve(bmp); }, reject);
          } catch (e) { URL.revokeObjectURL(url); reject(e); }
        };
        img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('svg_unreadable')); };
        img.src = url;
      });
    });
  }

  function isBlob(x) { return typeof Blob !== 'undefined' && x instanceof Blob; }
  function isImageData(x) { return typeof ImageData !== 'undefined' && x instanceof ImageData; }

  // { bmp, own } — `own` = we made it, we close it; a caller's bitmap is theirs.
  function sourceBitmap(src, kind) {
    if (isBlob(src)) {
      return (kind === 'svg' ? rasterizeSvg(src) : createImageBitmap(src)).then(function (b) { return { bmp: b, own: true }; });
    }
    if (isImageData(src)) return createImageBitmap(src).then(function (b) { return { bmp: b, own: true }; });
    return Promise.resolve({ bmp: src, own: false });   // ImageBitmap / canvas / <img>: drawable as is
  }

  function imageBitmap(src, opts) {
    var maxSide = opts && opts.maxSide;
    var kindP = isBlob(src) ? sniff(src).then(function (h) { return h.kind; }) : Promise.resolve('other');
    return kindP.then(function (kind) { return sourceBitmap(src, kind); }).then(function (s) {
      var b = s.bmp, long = Math.max(b.width || 0, b.height || 0);
      if (!maxSide || !long || long <= maxSide) return b;
      var k = maxSide / long;
      var cv = document.createElement('canvas');
      cv.width = Math.max(1, Math.round(b.width * k));
      cv.height = Math.max(1, Math.round(b.height * k));
      var cx = cv.getContext('2d');
      cx.imageSmoothingEnabled = true;
      cx.imageSmoothingQuality = 'high';
      cx.drawImage(b, 0, 0, cv.width, cv.height);
      if (s.own && b.close) b.close();
      return createImageBitmap(cv).then(function (small) { cv.width = cv.height = 0; return small; });
    });
  }

  // Rung-2 grids: 2×2 then 3×3 while a tile fits the pixel ceiling — and
  // finer grids for bigger photos, so every tile stays at NATIVE resolution
  // and none is larger than TILE_MAX_PIXELS. Up to 29.8 MP nothing changes.
  var TILE_OVERLAP = 0.25;
  function tilePixels(W, H, n) {
    return Math.min(W, W / n * (1 + 2 * TILE_OVERLAP)) * Math.min(H, H / n * (1 + 2 * TILE_OVERLAP));
  }
  function tileGrids(W, H) {
    var n = 2;
    while (n < 64 && tilePixels(W, H, n) > TILE_MAX_PIXELS) n++;
    return [n, n + 1];
  }

  /* ── Rung 1b: the whole picture again, the way a HARD photo reads ─────────
     (2026-10-29). On /qr-scanner 16.7 % of picture scans ended in «no code»
     (RU, 14 days), and 80 % of those sessions left with nothing. On the BoofCV
     QR set — 718 real photos in 16 conditions — the ladder read 74.9 %; every
     one of the 180 misses was then tried with each engine option and each
     preprocessing on its own (tests/playwright/qr-decode-wild-photo-recall-bench.mjs,
     the per-strategy pass in its header). What rescued, at ≤ 2048 px:
       tryDenoise (a closing on the binarised picture: broken, glared and noisy
       modules join up again) — 21; a light blur (sensor noise, moiré, JPEG
       blocks) — 15; a contrast stretch (washed out, under-exposed) — 8; and
       together 32. Another binarizer (GlobalHistogram 8, FixedThreshold 2),
       sharpening (2) and smaller scales alone (3–5) add nothing these three do
       not. Each pass costs one decode of a 2048 px picture (tens of ms) and runs
       only after the whole picture missed. */
  var RESCUE_SIDE = 2048;
  var RESCUE = [{ denoise: true }, { blur: 3 }, { stretch: true }];
  function grayPixels(id) {
    var d = id.data, n = id.width * id.height, g = new Uint8ClampedArray(n);
    for (var i = 0, j = 0; i < n; i++, j += 4) g[i] = (77 * d[j] + 150 * d[j + 1] + 29 * d[j + 2]) >> 8;
    return g;
  }
  // a box blur of radius r, rows then columns (≈ a Gaussian of σ ≈ r·0.6 per pass)
  function boxBlur(g, W, H, r) {
    var t = new Uint8ClampedArray(g.length), k = 2 * r + 1, x, y, s, row;
    for (y = 0; y < H; y++) {
      row = y * W; s = 0;
      for (x = -r; x <= r; x++) s += g[row + Math.min(W - 1, Math.max(0, x))];
      for (x = 0; x < W; x++) {
        t[row + x] = s / k;
        s += g[row + Math.min(W - 1, x + r + 1)] - g[row + Math.max(0, x - r)];
      }
    }
    for (x = 0; x < W; x++) {
      s = 0;
      for (y = -r; y <= r; y++) s += t[Math.min(H - 1, Math.max(0, y)) * W + x];
      for (y = 0; y < H; y++) {
        g[y * W + x] = s / k;
        s += t[Math.min(H - 1, y + r + 1) * W + x] - t[Math.max(0, y - r) * W + x];
      }
    }
  }
  // the darkest and the lightest 1 % pulled to black and white
  function stretchContrast(g) {
    var h = new Uint32Array(256), n = g.length, lo = 0, hi = 255, c = 0, v;
    for (var i = 0; i < n; i++) h[g[i]]++;
    for (v = 0; v < 256; v++) { c += h[v]; if (c > n * 0.01) { lo = v; break; } }
    c = 0;
    for (v = 255; v >= 0; v--) { c += h[v]; if (c > n * 0.01) { hi = v; break; } }
    if (hi - lo < 8) return;
    var k = 255 / (hi - lo);
    for (var j = 0; j < n; j++) g[j] = (g[j] - lo) * k;
  }
  function withGray(id, g) {
    var d = id.data;
    for (var i = 0, j = 0; i < g.length; i++, j += 4) { d[j] = d[j + 1] = d[j + 2] = g[i]; d[j + 3] = 255; }
    return id;
  }

  async function ladder(src, opts) {
    opts = opts || {};
    var ro = baseOpts(opts);
    var tiling = opts.tiling !== false;
    var bmp = null, ownBmp = false, cv = null, cx = null;

    function pixels(sx, sy, sw, sh, scale) {
      if (!cv) { cv = document.createElement('canvas'); cx = cv.getContext('2d', { willReadFrequently: true }); }
      cv.width = Math.max(1, Math.round(sw * scale));
      cv.height = Math.max(1, Math.round(sh * scale));
      cx.imageSmoothingEnabled = scale < 1;
      if (scale < 1) cx.imageSmoothingQuality = 'high';
      // Paper first: a see-through pixel is read by its colour, and an empty
      // canvas pixel is (0,0,0,0) — black. An opaque picture covers it anyway.
      cx.fillStyle = '#fff';
      cx.fillRect(0, 0, cv.width, cv.height);
      cx.drawImage(bmp, sx, sy, sw, sh, 0, 0, cv.width, cv.height);
      return cx.getImageData(0, 0, cv.width, cv.height);
    }
    async function read(id, map, o) {
      try { return mapResults(await decodeImageData(id, o || ro), map); }
      catch (e) { rethrowIfFatal(e); return []; }
    }
    async function needPixels(kind) {
      if (bmp) return true;
      try { var s = await sourceBitmap(src, kind); bmp = s.bmp; ownBmp = s.own; return true; }
      catch (e) {
        if (isMemoryError(e)) throw typed(e, 'MEMORY', 'qr-decode: the image does not fit in memory');
        return false;   // a picture the browser cannot open is a miss, as it always was
      }
    }
    // A weak symbology (WEAK above) stands only if the spot it was found at reads the
    // same text again at another scale; a hit without a position cannot be checked.
    async function confirmWeakHits(list) {
      if (!list.some(isWeak)) return list;
      var out = [];
      for (var q = 0; q < list.length; q++) {
        if (!isWeak(list[q])) { out.push(list[q]); continue; }
        if (!(await needPixels(head.kind))) continue;
        if (await reproduces(list[q], pixels, bmp.width, bmp.height, ro)) out.push(list[q]);
      }
      return out;
    }

    try {
      // Rung 1 — the whole frame. ~90% of real hits land here, fast. The
      // engine reads its own formats up to the ceiling; everything else, and
      // everything bigger, is read from pixels scaled to the ceiling.
      var r1 = [], head = { kind: 'other', w: 0, h: 0 };
      if (isImageData(src) && Math.max(src.width, src.height) <= FIRST_PASS_MAX_SIDE) {
        r1 = await read(src, null);
      } else {
        if (isBlob(src)) head = await sniff(src);
        if (isBlob(src) && ENGINE_FORMATS[head.kind] && !head.alpha && head.w && head.h && Math.max(head.w, head.h) <= FIRST_PASS_MAX_SIDE) {
          try { r1 = mapResults(await decodeBlob(src, ro), null); }
          catch (e) { rethrowIfFatal(e); r1 = []; }
        } else {
          if (!(await needPixels(head.kind))) return [];
          var s1 = Math.min(1, FIRST_PASS_MAX_SIDE / Math.max(bmp.width, bmp.height));
          r1 = await read(pixels(0, 0, bmp.width, bmp.height, s1), { x: 0, y: 0, s: s1 });
        }
      }
      r1 = await confirmWeakHits(dedupe(r1));
      if (r1.length || !tiling) return r1;

      // Need pixels at native size for the harder rungs.
      if (!(await needPixels(head.kind))) return r1;
      var W = bmp.width, H = bmp.height;

      // Rung 1b — the whole picture as a hard photo reads (RESCUE above).
      var sR = Math.min(1, RESCUE_SIDE / Math.max(W, H)), mapR = { x: 0, y: 0, s: sR };
      for (var ri = 0; ri < RESCUE.length; ri++) {
        var fx = RESCUE[ri], id = pixels(0, 0, W, H, sR), got1;
        if (fx.denoise) {
          got1 = await read(id, mapR, Object.assign({}, ro, { tryDenoise: true }));
        } else {
          var g = grayPixels(id);
          if (fx.blur) boxBlur(g, id.width, id.height, fx.blur);
          if (fx.stretch) stretchContrast(g);
          got1 = await read(withGray(id, g), mapR);
        }
        got1 = await confirmWeakHits(dedupe(got1));
        if (got1.length) return got1;
      }
      if (Math.min(W, H) < 160) return r1;

      // Rung 2 — overlapping tiles. 25% overlap so a code straddling a seam still
      // lands whole inside some tile.
      var grids = tileGrids(W, H);
      for (var gi = 0; gi < grids.length; gi++) {
        var n = grids[gi];
        var tw = W / n, th = H / n, found = [];
        for (var i = 0; i < n; i++) for (var j = 0; j < n; j++) {
          var sx = Math.max(0, tw * i - tw * TILE_OVERLAP), sy = Math.max(0, th * j - th * TILE_OVERLAP);
          var sw = Math.min(W - sx, tw * (1 + 2 * TILE_OVERLAP)), sh = Math.min(H - sy, th * (1 + 2 * TILE_OVERLAP));
          var got = await read(pixels(sx, sy, sw, sh, 1), { x: sx, y: sy, s: 1 });
          if (got.length) found = found.concat(got);
        }
        found = await confirmWeakHits(dedupe(found));
        if (found.length) return found;
      }

      // Rung 3 — upscale 2× so a tiny, low-module code gets interpolated detail.
      if (Math.max(W, H) <= 1600) {
        var up = await read(pixels(0, 0, W, H, 2), { x: 0, y: 0, s: 2 });
        up = await confirmWeakHits(dedupe(up));
        if (up.length) return up;
      }
      return r1;
    } catch (e) {
      // A canvas the browser could not allocate is the same "does not fit".
      if (isMemoryError(e) && e.code !== 'MEMORY') throw typed(e, 'MEMORY', 'qr-decode: the image does not fit in memory');
      throw e;
    } finally {
      if (bmp && ownBmp && bmp.close) { try { bmp.close(); } catch (e) {} }
      if (cv) { cv.width = cv.height = 0; }
    }
  }
})(window);
